import { NextPage } from 'next'
import Head from 'next/head'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OTCLandingData } from './types'
import { INITIAL_DATA } from './constants'
import { useRouter } from 'next/router'
import trace from '~/ga'
import {
  StyledOTCApplyMessage,
  StyledOTCButton,
  StyledOTCInputArea,
  StyledOTCIntroduce,
  StyledOTCLanding,
} from './styled'
import { PositiveInput } from '../Input'
import {
  getCachedCurrencies,
  getExchangeRate,
  getPublishableApiKey,
} from './moonpay'
import { formatAmount } from '../Exchange/helpers'

const OTCLanding: NextPage = () => {
  const { t } = useTranslation()
  const [amount, setAmount] = useState<string>('100')
  const [data, setData] = useState<OTCLandingData>(INITIAL_DATA)
  const { query } = useRouter()

  const displayValidateKey = useMemo(
    () => (+amount < 50 ? 'otc_min_buy' : +amount > 2000 ? 'otc_max_buy' : ''),
    [amount],
  )

  useEffect(() => {
    let { symbol, address } = query
    if (!symbol || Array.isArray(symbol)) return

    const getData = async () => {
      symbol = symbol as string
      address = address as string

      let symbolLowerCased = symbol.toLowerCase()
      let currencies = []
      let rateObj = {} as any
      let precision = 4
      try {
        currencies = await getCachedCurrencies()
        if (currencies.find((c) => c.code === symbolLowerCased) === -1) {
          symbol = 'ETH'
          symbolLowerCased = 'eth'
        }

        rateObj = await getExchangeRate(symbolLowerCased, window.location.host)
        precision = currencies.find((c) => c.code === symbolLowerCased)
          .precision
      } catch (e) {}
      setData({
        symbol,
        currencies,
        rate: rateObj.USD,
        address,
        precision,
      })
    }
    getData()
  }, [query])

  const handleToOtc = () => {
    if (!amount) return
    const { symbol } = data
    trace.click('click_otc_landing_submit', { symbol })

    const originalUrl = `https://buy.moonpay.io?apiKey=${getPublishableApiKey()}&currencyCode=${symbol.toLowerCase()}&walletAddress=&baseCurrencyAmount=${amount}&baseCurrencyCode=USD`

    window.location.href = originalUrl
  }

  return (
    <>
      <Head>
        <title>
          {t('otc_buy')} {data.symbol}
        </title>
        <meta name="description" content={t('meta_common_description')} />
        <meta name="keywords" content={t('meta_common_keywords')} />

        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width, user-scalable=no"
        />
      </Head>
      <StyledOTCLanding>
        <StyledOTCInputArea>
          <PositiveInput
            autoFocus={true}
            className="otc-input"
            onChange={setAmount}
            value={amount}
          />
          <span className="otc-input-label">USD</span>
        </StyledOTCInputArea>
        {displayValidateKey ? (
          <StyledOTCApplyMessage danger>
            {t(displayValidateKey)}
          </StyledOTCApplyMessage>
        ) : (
          <StyledOTCApplyMessage>
            ≈ {formatAmount(+amount / Number(data.rate), data.precision)}{' '}
            {data.symbol}
          </StyledOTCApplyMessage>
        )}
        <StyledOTCButton
          active={Boolean(!displayValidateKey && amount)}
          onClick={!displayValidateKey && amount ? handleToOtc : null}
        >
          {t('otc_next')}
        </StyledOTCButton>

        <StyledOTCIntroduce>
          <li>{t('otc_tip_1')}</li>
        </StyledOTCIntroduce>
      </StyledOTCLanding>
    </>
  )
}

export default OTCLanding
